/**
 * Engine config files. The shell owns these — the player never edits an ini.
 *
 * fallout2-ce contains the modern high-resolution patch behavior natively.
 * The shell selects a logical render resolution here; hud.ts then fits that
 * framebuffer to the browser using exact device-pixel scaling where possible.
 */

import { getActiveGame } from './registry';

export type GameType = 'fo1' | 'fo2';

export function homeDir(game: GameType): string {
  return game === 'fo1' ? '/fallout1-ce' : '/fallout2-ce';
}

export function saveDir(game: GameType): string {
  return `${homeDir(game)}/data/SAVEGAME`;
}

/** Emscripten names the IDBFS database after its mount point. */
export function idbfsDbName(game: GameType): string {
  return saveDir(game);
}

/**
 * How the shell decides *which* game's paths it is talking about.
 *
 * There used to be `HOME`/`SAVE_DIR`/`IDBFS_DB_NAME` module constants pinned to
 * `/fallout2-ce`, and every consumer that imported them (the save archive, the
 * durability probe, the item catalog, the mods layer) silently walked the
 * Fallout 2 tree on a Fallout 1 install — which is how an FO1 player got ten
 * empty save slots. They are gone; the rule that replaced them is:
 *
 *   - a function that already has a `GameType` in hand takes it as a parameter
 *     and calls `homeDir()` / `saveDir()` directly. `engine.ts` is the example:
 *     `boot()` is told the game type by its caller.
 *   - everything else — modules reached from a UI click, with no game in
 *     scope and no honest way to thread one through without touching every
 *     call site — resolves it *at call time* through `activeHome()` /
 *     `activeSaveDir()` / `activeIdbfsDbName()` below. Never hoist those into a
 *     module-level `const`: that recaptures the pin this comment exists to
 *     describe.
 *
 * The answer comes from `registry.ts` (which vault the shell opened), with a
 * boot-time override so the engine's own notion of the running game wins over
 * whatever localStorage last said.
 */
let runtimeGame: GameType | null = null;

/** Pin the game the shell is currently operating on. Called from boot and the gate. */
export function setRuntimeGame(game: GameType): void {
  runtimeGame = game;
}

export function activeGameType(): GameType {
  return runtimeGame ?? getActiveGame() ?? 'fo2';
}

export const activeHome = (): string => homeDir(activeGameType());
export const activeSaveDir = (): string => saveDir(activeGameType());
export const activeIdbfsDbName = (): string => idbfsDbName(activeGameType());

/** The engine's config file name — `fallout.cfg` on FO1, `fallout2.cfg` on FO2. */
export const cfgFileNameFor = (game: GameType): string =>
  game === 'fo1' ? 'fallout.cfg' : 'fallout2.cfg';

export const BASE_WIDTH = 640;
export const BASE_HEIGHT = 480;

export interface DisplayMode {
  id: string;
  label: string;
  width: number;
  height: number;
}

/**
 * The presentation surface, measured in *device* pixels.
 *
 * Everything in this file is derived from device pixels rather than CSS pixels.
 * On a Retina Mac (dpr 2) a CSS-pixel derivation is off by a factor of two in
 * both axes, which is exactly how a window that could show 864x558 of map ended
 * up rendering 640x480 in the middle of a field of black.
 */
export interface Viewport {
  deviceW: number;
  deviceH: number;
  dpr: number;
}

/**
 * @param fullscreen  measure the *screen* rather than the window. Used both
 *   while actually fullscreen and to answer "what would fullscreen give me",
 *   which is how the boot path can size the framebuffer for a fullscreen
 *   session before the fullscreen request has been granted.
 */
export function currentViewport(fullscreen = !!document.fullscreenElement): Viewport {
  const dpr = window.devicePixelRatio || 1;
  // A backgrounded or not-yet-laid-out tab reports a zero viewport. Sizing a
  // framebuffer from that yields a 640×480 floor and a screen full of black, so
  // fall back to the screen rather than believing it.
  const winW = window.innerWidth || document.documentElement.clientWidth || 0;
  const winH = window.innerHeight || document.documentElement.clientHeight || 0;
  const w = fullscreen || !winW ? window.screen?.width || winW || 1280 : winW;
  const h = fullscreen || !winH ? window.screen?.height || winH || 720 : winH;
  return {
    deviceW: Math.max(1, Math.round(w * dpr)),
    deviceH: Math.max(1, Math.round(h * dpr)),
    dpr,
  };
}

/**
 * Resolution is not a zoom level, and raising it alone is a trap.
 *
 * The engine renders the world at `[screen] resolution_x/y`, and Fallout 2's
 * interface art is a fixed 640 pixels wide no matter what. So a bigger
 * framebuffer shows more map but also makes the HUD *physically smaller* on the
 * same monitor — at 1280 internal presented 1:1, the interface is half the size
 * it is at 4:3. That is the "everything is tiny and the map is cut off"
 * failure, and it is why every mode here is a pair: an internal resolution AND
 * the display scale used to present it.
 *
 * The magnification is the axis the player actually feels, so it is the axis
 * the modes are defined on. The CSS box always aspect-fits the available
 * surface; `image-rendering: pixelated` keeps the result sharp without leaving
 * a 640×480 framebuffer stranded at 1× merely because the exact fit is 1.5×.
 *
 * Engine constraints, from engine/src/settings.cc:
 *   resolution_x  clamp(640, 7680)
 *   resolution_y  clamp(480, 4320)
 *   scale         clamp(1, 4)
 * Note `scale` is a *divisor*: engine/src/svga.cc `_GNW95_init_mode_ex` treats
 * resolution_x/y as the window and renders internally at resolution/scale,
 * refusing to scale at all if that would fall under 640×480. We keep scale=1
 * and magnify in CSS instead, so the framebuffer stays small and the
 * device-pixel mapping stays exact.
 */

/**
 * Past this, the fixed-size interface art is unusable — 640px of HUD on a
 * 1920px-wide render is already a third of the screen and is as far as "more
 * map" is worth pushing.
 */
export const MAX_INTERNAL_WIDTH = 1920;
export const MAX_INTERNAL_HEIGHT = 1200;

/**
 * SDL can give the canvas a device-pixel drawing buffer: ask for a 852×480
 * window on a 2× display and `canvas.width` may come back 1704. `hud.ts`
 * measures that buffer scale so the CSS box is derived from game pixels rather
 * than accidentally treating the high-DPI backing store as a larger world.
 */
/** Framebuffers must be even in both axes. */
const even = (n: number) => Math.max(2, Math.floor(n / 2) * 2);
const clampEven = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, even(n)));

interface DisplayModeSpec {
  id: string;
  name: string;
  blurb: string;
  /** Fixed size (4:3 only) — magnified by whatever factor fits. */
  fixed?: { width: number; height: number };
  /** Target CSS magnification. `k` is this times the device pixel ratio. */
  cssScale?: number;
}

/**
 * Only 4:3 is a fixed resolution. The other two are derived from both
 * viewport axes with one shared scale, so their framebuffer has the viewport's
 * aspect ratio and expands the map into widescreen space.
 */
export const DISPLAY_MODE_SPECS: readonly DisplayModeSpec[] = [
  {
    id: 'classic',
    name: '4:3',
    blurb: 'Exactly as shipped, magnified to fit.',
    fixed: { width: BASE_WIDTH, height: BASE_HEIGHT },
  },
  { id: 'comfort', name: 'Widescreen', blurb: 'Widescreen with a comfortably large interface.', cssScale: 1.5 },
  { id: 'fit', name: 'Max', blurb: 'The widest view with the smallest interface.', cssScale: 1 },
];

/** The 2× recipe: what a first-time player should get without touching a menu. */
export const DEFAULT_DISPLAY_MODE = 'comfort';

/**
 * The requested magnification, walked to the nearest one the surface can
 * actually deliver.
 *
 * A clamp that bites is a border: on a 720-tall window, "half the display"
 * asks for a 360-tall framebuffer, the engine's 480 floor pushes it back up,
 * and the result is 640×480 stranded in the middle of the screen — precisely
 * the reported bug. Stepping the magnification down (or up, against the cap)
 * keeps `internal × m × dpr` equal to the surface instead.
 */
function deriveMode(spec: DisplayModeSpec, vp: Viewport): DisplayMode {
  const cssWidth = vp.deviceW / vp.dpr;
  const cssHeight = vp.deviceH / vp.dpr;
  const desiredScale = Math.max(1, spec.cssScale ?? 1);

  // Clamp the *shared scale*, not the two dimensions independently. Separate
  // dimension clamps turn a 16:9 request into 640x480 as soon as either axis
  // reaches the engine floor, which is the source of the side letterboxing.
  const smallestScaleAllowedByCaps = Math.max(
    cssWidth / MAX_INTERNAL_WIDTH,
    cssHeight / MAX_INTERNAL_HEIGHT
  );
  const largestScaleAllowedByFloors = Math.min(
    cssWidth / BASE_WIDTH,
    cssHeight / BASE_HEIGHT
  );
  const cssScale = Math.max(
    smallestScaleAllowedByCaps,
    Math.min(desiredScale, largestScaleAllowedByFloors)
  );
  const width = clampEven(cssWidth / cssScale, BASE_WIDTH, MAX_INTERNAL_WIDTH);
  const height = clampEven(cssHeight / cssScale, BASE_HEIGHT, MAX_INTERNAL_HEIGHT);
  return { id: spec.id, label: `${spec.name} — ${width} × ${height}`, width, height };
}

export function resolveDisplayMode(id: string, vp: Viewport = currentViewport()): DisplayMode {
  const spec = DISPLAY_MODE_SPECS.find((s) => s.id === id) ?? DISPLAY_MODE_SPECS[0];
  if (spec.fixed) {
    return { id: spec.id, label: `${spec.name} — ${spec.fixed.width} × ${spec.fixed.height}`, ...spec.fixed };
  }
  return deriveMode(spec, vp);
}

export interface PresentedFit {
  /** CSS magnification of the drawing buffer; fractional values are allowed. */
  m: number;
  /** Device pixels per game pixel. */
  k: number;
  /** Canvas box, in CSS pixels. */
  cssWidth: number;
  cssHeight: number;
  /** Unused surface, in device pixels. */
  letterboxW: number;
  letterboxH: number;
}

/**
 * The single definition of how a framebuffer is presented. `ui/hud.ts` applies
 * it and the settings UI quotes it, so the number on screen is the number the
 * canvas actually gets.
 *
 * @param bufferScale  device pixels per game pixel *inside* the drawing buffer.
 *   SDL sets this to the device pixel ratio; `hud.ts` measures it rather than
 *   assuming, because getting it wrong is the difference between a crisp image
 *   and a resampled one.
 */
export function presentedFit(
  width: number,
  height: number,
  vp: Viewport = currentViewport(),
  bufferScale = vp.dpr
): PresentedFit {
  const bufW = width * bufferScale;
  const bufH = height * bufferScale;
  const m = Math.min(vp.deviceW / bufW, vp.deviceH / bufH);
  return {
    m,
    k: m * bufferScale,
    cssWidth: (bufW * m) / vp.dpr,
    cssHeight: (bufH * m) / vp.dpr,
    letterboxW: vp.deviceW - bufW * m,
    letterboxH: vp.deviceH - bufH * m,
  };
}


function setIniValue(text: string, section: string, key: string, value: string | number): string {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const sectionName = section.toLowerCase();
  const keyName = key.toLowerCase();
  let sectionIndex = lines.findIndex((line) => line.trim().toLowerCase() === `[${sectionName}]`);
  if (sectionIndex < 0) {
    if (lines.at(-1)?.trim()) lines.push('');
    lines.push(`[${section}]`, `${key}=${value}`);
    return lines.join('\n');
  }
  let end = lines.length;
  for (let i = sectionIndex + 1; i < lines.length; i++) {
    if (/^\s*\[.+\]\s*$/.test(lines[i])) { end = i; break; }
  }
  for (let i = sectionIndex + 1; i < end; i++) {
    const candidate = lines[i].split('=', 1)[0].trim().toLowerCase();
    if (candidate === keyName) {
      lines[i] = `${key}=${value}`;
      return lines.join('\n');
    }
  }
  lines.splice(end, 0, `${key}=${value}`);
  return lines.join('\n');
}

export function applyDisplayMode(cfg: string, mode: DisplayMode): string {
  let out = cfg;
  out = setIniValue(out, 'screen', 'resolution_x', mode.width);
  out = setIniValue(out, 'screen', 'resolution_y', mode.height);
  out = setIniValue(out, 'screen', 'windowed', 1); // DOM fullscreen owns presentation.
  out = setIniValue(out, 'screen', 'scale', 1);
  out = setIniValue(out, 'ui', 'main_menu_scale_mode', mode.id === 'classic' ? 1 : 2);
  out = setIniValue(out, 'ui', 'movie_aspect_fit', 1);
  out = setIniValue(out, 'ui', 'enable_high_resolution_stencil', 1);
  return out;
}

/**
 * Force engine debugPrint output to somewhere we can actually see it.
 *
 * Like the display knobs, this has to be re-stamped on every boot: the engine
 * rewrites fallout2.cfg on exit, and currentCfgText() prefers that stored copy,
 * so an install that has already played would keep its original
 * `mode=environment` forever and stay silent no matter what fallout2Cfg() says.
 */
export function applyDebugMode(cfg: string): string {
  return setIniValue(cfg, 'debug', 'mode', 'screen');
}

export interface EngineSettings {
  /** 0 = play intro movies, 2 = skip them (set after the first successful run). */
  skipOpeningMovies: 0 | 2;
  music: boolean;
  sounds: boolean;
  speech: boolean;
  subtitles: boolean;
  gameDifficulty: 0 | 1 | 2;
  violenceLevel: 0 | 1 | 2 | 3;
}

export const DEFAULT_SETTINGS: EngineSettings = {
  skipOpeningMovies: 0,
  music: true,
  sounds: true,
  speech: true,
  subtitles: false,
  gameDifficulty: 1,
  violenceLevel: 3,
};

const b = (v: boolean) => (v ? 1 : 0);

// Note on [debug] mode: the engine's "environment" setting looks for a
// DEBUGACTIVE env var, which nothing in our build sets — so gDebugPrintProc
// stays null and every debugPrint is silently discarded. "screen" routes it
// through printf → stdout → Emscripten's Module.print, which engine.ts
// forwards to onLog, so save-load rejection reasons (the phase-0 hardening's
// "Error: obj_load:" lines, say) actually reach the console.
export function fallout2Cfg(s: EngineSettings): string {
  return `[system]
executable=game
master_dat=master.dat
master_patches=data
critter_dat=critter.dat
critter_patches=data
language=english
art_cache_size=8
color_cycling=1
free_space=20480
hashing=1
splash=0
interrupt_walk=1
[preferences]
game_difficulty=${s.gameDifficulty}
combat_difficulty=1
violence_level=${s.violenceLevel}
running=0
subtitles=${b(s.subtitles)}
combat_looks=1
combat_messages=1
combat_speed=0
combat_taunts=1
item_highlight=1
target_highlight=2
language_filter=0
mouse_sensitivity=1.000000
brightness=1.000000
player_speedup=0
text_base_delay=3.500000
text_line_delay=1.000000
[sound]
initialize=1
music=${b(s.music)}
sounds=${b(s.sounds)}
speech=${b(s.speech)}
master_volume=22281
music_volume=22281
sndfx_volume=22281
speech_volume=22281
cache_size=448
music_path1=data/sound/music/
music_path2=data/sound/music/
device=-1
port=-1
irq=-1
dma=-1
[ui]
skip_opening_movies=${s.skipOpeningMovies}
main_menu_scale_mode=1
movie_aspect_fit=1
enable_high_resolution_stencil=1
touch_tap_assist=1
touch_double_tap_assist=1
touch_smart_cursor=1
touch_drag_pan=1
[debug]
mode=screen
[screen]
resolution_x=${BASE_WIDTH}
resolution_y=${BASE_HEIGHT}
windowed=1
scale=1
`;
}

export function falloutCfg(s: EngineSettings): string {
  return `[system]
executable=game
master_dat=master.dat
master_patches=data
critter_dat=critter.dat
critter_patches=data
language=english
scroll_lock=0
interrupt_walk=1
art_cache_size=8
color_cycling=1
hashing=1
splash=0
free_space=20480
[preferences]
game_difficulty=${s.gameDifficulty}
combat_difficulty=1
violence_level=${s.violenceLevel}
target_highlight=2
item_highlight=1
running_burning_guy=1
combat_messages=1
combat_taunts=1
language_filter=0
running=0
subtitles=${b(s.subtitles)}
combat_speed=0
player_speedup=0
text_base_delay=3.500000
text_line_delay=1.399994
brightness=1.000000
mouse_sensitivity=1.000000
[sound]
initialize=1
device=-1
port=-1
irq=-1
dma=-1
sounds=${b(s.sounds)}
music=${b(s.music)}
speech=${b(s.speech)}
master_volume=22281
music_volume=22281
sndfx_volume=22281
speech_volume=22281
cache_size=448
music_path1=sound\\music\\
music_path2=sound\\music\\
[debug]
mode=screen
show_tile_num=0
show_script_messages=0
show_load_info=0
output_map_data_info=0
[qol]
action_anim_speed=1.750000
movement_anim_speed=1.000000
skip_cosmetic_ceremony=1
double_click_transfer=1
double_click_equip=1
smart_take_all=1
save_pages=5
autosave_exit_grid=1
`;
}

/**
 * fallout1-ce reads its window size from f1_res.ini at startup (clamped to a
 * 640x480 minimum), so this is the control for Fallout 1's display mode the
 * way [screen] resolution_x/y is for Fallout 2.
 */
export function f1ResIni(width = BASE_WIDTH, height = BASE_HEIGHT): string {
  return `[MAIN]
SCR_WIDTH=${Math.max(640, width)}
SCR_HEIGHT=${Math.max(480, height)}
WINDOWED=1
SCALE_2X=0
`;
}

export function f2ResIni(width = BASE_WIDTH, height = BASE_HEIGHT): string {
  return `[MAIN]
SCR_WIDTH=${width}
SCR_HEIGHT=${height}
WINDOWED=1
COLOUR_BITS=32
GRAPHICS_MODE=1
SCALE_2X=0
F2_RES_DAT=f2_res.dat
F2_RES_PATCHES=data
[MAPS]
EDGE_CLIPPING_ON=1
SCROLL_DIST_X=HALF_SCRN
SCROLL_DIST_Y=HALF_SCRN
NumPathNodes=20
[STATIC_SCREENS]
SPLASH_SCRN_SIZE=1
[OTHER_SETTINGS]
SPLASH_SCRN_TIME=0
CPU_USAGE_FIX=1
`;
}
